import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import { handleMouseDown } from "@faire/retailer-visitor-shared/lib/handleMouseDown";
import { useIsSearchPage } from "@faire/retailer-visitor-shared/lib/useIsSearchPage";
import { useAssignSettingEnableOnMouseDownFilters, useGetSettingEnableOnMouseDownFilters } from "@faire/retailer-visitor-shared/settings/useSettingEnableOnMouseDownFilters";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { useIsLoggedOutDiscoverPage } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Pill } from "@faire/web--source/slate/Filter/styled";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { Filter as FilterIcon } from "@faire/web--source/slate/icons/foundation/Filter";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackLoggedOutRetailersDiscoverPageAllFiltersPillClick } from "@faire/web-api--source/events/loggedOutRetailers/click/discoverPageAllFiltersPill";
import * as React from "react";
import styled, { css } from "styled-components";
// toggling sidebar will cause layout shifts, we want to wait 1s for it
// during which we prevent the top nav from hiding:
var TIME_TO_WAIT_FOR_LAYOUT_SHIFTS = 1000;
export var ToggleSidebarFiltersPill = function() {
    var _useFilterStore = useFilterStore([
        "activeFilterCount",
        "isSideBarVisible"
    ]), _useFilterStore_data = _useFilterStore.data, activeFilterCount = _useFilterStore_data.activeFilterCount, isSideBarVisible = _useFilterStore_data.isSideBarVisible, setFilterState = _useFilterStore.setFilterState;
    var setShouldKeepTopNavVisible = useTopOfPageContent().setShouldKeepTopNavVisible;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isLoggedOutDiscoverPage = useIsLoggedOutDiscoverPage();
    var assignEnableOnMouseDownFilters = useAssignSettingEnableOnMouseDownFilters();
    var isSearchPage = useIsSearchPage();
    var isMouseDownEnabled = useGetSettingEnableOnMouseDownFilters() && isSearchPage;
    var hasFilterSelected = activeFilterCount > 0;
    var showAppliedState = isSideBarVisible || hasFilterSelected;
    var textColor = showAppliedState ? Core.text.inverse : Core.text.default;
    var iconColor = showAppliedState ? Core.icon.inverse : Core.icon.default;
    var onToggleClick = function() {
        if (isLoggedOutDiscoverPage) {
            trackLoggedOutRetailersDiscoverPageAllFiltersPillClick(isSideBarVisible ? "close_filters" : "open_filters");
        }
        setShouldKeepTopNavVisible(true);
        setFilterState(function(currentState) {
            return _object_spread_props(_object_spread({}, currentState), {
                isSideBarVisible: !isSideBarVisible,
                isNewFilterSelected: false
            });
        });
        setTimeout(function() {
            setShouldKeepTopNavVisible(false);
        }, TIME_TO_WAIT_FOR_LAYOUT_SHIFTS);
    };
    var handleOnMouseDown = function() {
        if (isSearchPage) {
            assignEnableOnMouseDownFilters();
            if (isMouseDownEnabled) {
                onToggleClick();
            }
        }
    };
    if (activeFilterCount < 0) {
        return null;
    }
    return /*#__PURE__*/ _jsx(StyledRow, {
        children: /*#__PURE__*/ _jsxs(StyledPill, {
            isOpen: false,
            alwaysShowCloseButton: false,
            hasFilterSelected: isSideBarVisible,
            onClick: isMouseDownEnabled ? undefined : onToggleClick,
            onMouseDown: handleMouseDown(handleOnMouseDown),
            onKeyDown: handleKeyDown(onToggleClick),
            role: "button",
            tabIndex: 0,
            "aria-label": !isSideBarVisible ? strictLocalize({
                id: "DXjGv5",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Open filters sidebar"
                    }
                ]
            }) : strictLocalize({
                id: "T5mV9l",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Close filters sidebar"
                    }
                ]
            }),
            $showAppliedState: showAppliedState,
            children: [
                isSideBarVisible ? /*#__PURE__*/ _jsx(Chevron, {
                    direction: "left",
                    color: iconColor
                }) : null,
                /*#__PURE__*/ _jsx(Spacer, {
                    width: 4
                }),
                isSideBarVisible ? /*#__PURE__*/ _jsx(StyledTypography, {
                    color: textColor,
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "KsOZsX",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Hide filters"
                            }
                        ]
                    })
                }) : /*#__PURE__*/ _jsxs(StyledTypography, {
                    color: textColor,
                    children: [
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "iBKdru",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "All filters"
                                }
                            ]
                        }),
                        hasFilterSelected ? " (".concat(activeFilterCount, ")") : null
                    ]
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    width: 4
                }),
                !isSideBarVisible ? /*#__PURE__*/ _jsx(FilterIcon, {
                    fontSize: "24px",
                    color: iconColor
                }) : null
            ]
        })
    });
};
var StyledRow = styled(Row).withConfig({
    componentId: "sc-f3654f13-0"
})([
    "min-width:max-content;padding:",
    ";"
], getSpacing("2x", "1x", "1x", 0));
var StyledPill = styled(Pill).withConfig({
    componentId: "sc-f3654f13-1"
})([
    "padding:",
    ";",
    ";&:hover{background-color:",
    ";border-color:",
    ";}"
], getSpacing(0, 10), function(param) {
    var $showAppliedState = param.$showAppliedState;
    return $showAppliedState ? css([
        "color:",
        ";background-color:",
        ";"
    ], Core.text.inverse, Core.surface.default) : css([
        "border:1px solid ",
        ";background-color:",
        ";"
    ], Core.border.muted, Core.surface.inverse);
}, function(param) {
    var $showAppliedState = param.$showAppliedState;
    return $showAppliedState ? Core.surface.default : Core.surface.inverse;
}, Core.border.default);
var StyledTypography = styled(Typography).withConfig({
    componentId: "sc-f3654f13-2"
})([
    "width:max-content;"
]);
